/**
 * Drill-down từ một con số thống kê ra ĐÚNG danh sách lịch hẹn tạo nên nó (#19863 / STAT-30).
 *
 * Link chỉ mang TÊN chỉ số, kỳ, khoá nhóm và mốc thời gian của lúc tính con số — không mang điều
 * kiện lọc nào, và không mang PHI (chỉ id danh mục, mã trạng thái, ngày). Server dựng lại đúng tập
 * mà báo cáo đã đếm (`AppointmentMetric` của domain-scheduling), nên client không viết lại định
 * nghĩa của chỉ số nào.
 */
import { apiClient } from "./client";
import { downloadServerCsv } from "./reports";
import type { PageResponse } from "../types/patient";
import type { AppointmentSummary } from "../types/appointment";

/** Mã chỉ số — trùng `AppointmentMetric.code()` ở server. */
export type DrillMetric =
  | "scheduled"
  | "active"
  | "requested"
  | "confirmed"
  | "checked_in"
  | "completed"
  | "cancelled"
  | "no_show"
  | "attended_past"
  | "no_show_past"
  | "not_closed"
  | "created"
  | "visits"
  | "pending_completion"
  | "waiting_now"
  | "late"
  | "no_room";

/** Chiều lọc — trùng `AppointmentDimension.param()`. `"none"` = khoá rỗng (chưa gán). */
export type DrillKey =
  | "status"
  | "channel"
  | "department"
  | "doctor"
  | "branch"
  | "room"
  | "service"
  | "hour"
  | "dow";

export interface DrillQuery {
  metric: DrillMetric;
  fromDate: string;
  toDate: string;
  /** "Hôm nay" của lúc tính con số — chỉ số "trước hôm nay" cần nó. */
  asOf?: string;
  /** Thời điểm của lúc tính con số — "quá giờ" cần nó. */
  at?: string;
  keys?: Partial<Record<DrillKey, string>>;
}

export const drillMetricLabel: Record<DrillMetric, string> = {
  scheduled: "Lịch theo ngày khám",
  active: "Lịch hẹn còn hiệu lực (không gồm lịch đã huỷ)",
  requested: "Lịch chờ xác nhận",
  confirmed: "Lịch đã xác nhận",
  checked_in: "Lịch đã tiếp đón",
  completed: "Lịch đã khám xong",
  cancelled: "Lịch đã huỷ",
  no_show: "Lịch không đến",
  attended_past: "Lịch đã đến (các ngày trước hôm nay)",
  no_show_past: "Lịch không đến (các ngày trước hôm nay)",
  not_closed: "Lịch đã qua ngày mà chưa đóng",
  created: "Lịch được tạo trong kỳ",
  visits: "Lượt khám đã hoàn thành",
  pending_completion: "Đã đến nhưng bệnh án chưa ký",
  waiting_now: "Đang chờ khám",
  late: "Quá giờ hẹn mà chưa tới",
  no_room: "Chưa phân phòng",
};

const METRICS = Object.keys(drillMetricLabel) as DrillMetric[];
const KEYS: readonly DrillKey[] = [
  "status",
  "channel",
  "department",
  "doctor",
  "branch",
  "room",
  "service",
  "hour",
  "dow",
];

/** Ngày YYYY-MM-DD của `d` theo giờ Việt Nam — "hôm nay" mà server dùng khi đếm (asOf). */
export function vnIsoDate(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Khoá NULL của một nhóm ("chưa gán khoa") — server lọc `IS NULL`. */
export const NONE = "none";

/**
 * Link tới danh sách lịch hẹn ở chế độ drill-down. `asOf` / `at` mặc định là lúc dựng link — truyền
 * thời điểm số liệu được tính (dataUpdatedAt) khi có, để danh sách mở ra muộn vẫn là tập đã đếm.
 */
export function drillLink(q: DrillQuery): string {
  const p = new URLSearchParams({
    metric: q.metric,
    fromDate: q.fromDate,
    toDate: q.toDate,
  });
  p.set("asOf", q.asOf ?? vnIsoDate(new Date()));
  p.set("at", q.at ?? new Date().toISOString());
  for (const k of KEYS) {
    const v = q.keys?.[k];
    if (v != null && v !== "") p.set(k, v);
  }
  return `/appointments/list?${p.toString()}`;
}

/** Đọc chế độ drill-down từ URL; `null` khi URL không phải một link drill-down hợp lệ. */
export function parseDrill(params: URLSearchParams): DrillQuery | null {
  const metric = params.get("metric") as DrillMetric | null;
  const fromDate = params.get("fromDate");
  const toDate = params.get("toDate");
  if (!metric || !METRICS.includes(metric) || !fromDate || !toDate) return null;
  const keys: Partial<Record<DrillKey, string>> = {};
  for (const k of KEYS) {
    const v = params.get(k);
    if (v) keys[k] = v;
  }
  return {
    metric,
    fromDate,
    toDate,
    asOf: params.get("asOf") ?? undefined,
    at: params.get("at") ?? undefined,
    keys,
  };
}

/** Tham số của một tập drill-down — CÙNG một bộ cho danh sách và cho tệp xuất. */
function drillParams(q: DrillQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    metric: q.metric,
    fromDate: q.fromDate,
    toDate: q.toDate,
  };
  if (q.asOf) params.asOf = q.asOf;
  if (q.at) params.at = q.at;
  for (const k of KEYS) {
    const v = q.keys?.[k];
    if (v) params[k] = v;
  }
  return params;
}

export const drillDownApi = {
  list(
    q: DrillQuery,
    page: number,
    size: number,
  ): Promise<PageResponse<AppointmentSummary>> {
    const params = { ...drillParams(q), page, size };
    return apiClient
      .get<
        PageResponse<AppointmentSummary>
      >("/v1/appointments/drill-down", { params })
      .then((r) => r.data);
  },
  /**
   * Xuất CSV đúng tập này PHÍA SERVER (#19865 / STAT-32): cùng quyền, cùng phạm vi, có dòng kiểm
   * toán, tối đa 5.000 dòng (quá thì lỗi có câu giải thích).
   */
  exportCsv(q: DrillQuery): Promise<void> {
    return downloadServerCsv(
      "/v1/appointments/drill-down/export",
      drillParams(q),
      `lich-hen-${q.metric}-${q.fromDate}_${q.toDate}.csv`,
    );
  },
};

export const drillDownQueryKeys = {
  list: (q: DrillQuery, page: number) =>
    ["appointments", "drill-down", q, page] as const,
};
